import { useEffect, useRef } from 'react'

interface Spot { x: number; y: number }
interface Layout { sig: string; byPlace: Map<string, Spot>; byCard: Map<string, Spot> }

const FLY_MS = 560
const SLIDE_MS = 420
const EASE = 'cubic-bezier(.2, .8, .2, 1)'

const reducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * The Board's cards travel instead of jumping. Whenever WHICH cards are where changes (the
 * places, `section|id`, in page order), each card moves from where it was to where it is now:
 *   - a card already on the page slides to its new spot (the deck closing a gap, Recent making
 *     room for an app that came back to its front);
 *   - a card new to its section flies in from where the same app was a moment ago — a fresh
 *     favorite from its Recent or collection card, an un-favorited one to the front of Recent.
 * A card that only appears (nothing to fly from — a search result, Show more) just appears.
 *
 * Watched on the DOM (a MutationObserver: cards added, removed, or given another place), not on
 * this component's renders — a section can re-render on its own, and a render-driven check then
 * finds the cards already moved. The observer runs before the frame is painted, so each card
 * still starts from where it was. Positions are taken inside the scroll area (a scroll isn't a
 * move); a resize (and the reflow it causes for half a second) re-measures without moving anything. Off while `paused` (the deal-in, arranging
 * the deck) and with reduced motion.
 */
export function useCardFlight(root: HTMLElement | null, paused: boolean, openCardId?: { readonly current: string | null }): void {
  const pausedRef = useRef(paused)
  pausedRef.current = paused
  // The app whose card is open (turned over in the centre): its cards don't travel — the person is
  // looking at the card itself, so a copy flying underneath it is noise. They just take their new
  // slot; the cards around them still slide to make room or close the gap.
  const openRef = useRef(openCardId)
  openRef.current = openCardId
  useEffect(() => {
    if (!root) return
    let last: Layout | null = null
    // A resize reflows the page (the Recent row fits fewer cards, the columns change): the
    // cards just go where they belong — only what the person did travels.
    let resizedAt = -Infinity
    const measure = (): Layout => {
      const cards = Array.from(root.querySelectorAll<HTMLElement>('.pdc[data-place]'))
      const box = root.getBoundingClientRect()
      const byPlace = new Map<string, Spot>()
      const byCard = new Map<string, Spot>()
      for (const el of cards) {
        // Its place in the layout: the centre (a flight's scale doesn't move it) minus the
        // flight's own offset, so a card mid-flight is measured where it is going, not drawn.
        const r = el.getBoundingClientRect()
        const m = new DOMMatrixReadOnly(getComputedStyle(el).transform === 'none' ? undefined : getComputedStyle(el).transform)
        const s = { x: r.left + r.width / 2 - m.m41 - box.left, y: r.top + r.height / 2 - m.m42 - box.top + root.scrollTop }
        byPlace.set(el.dataset.place!, s)
        const id = el.dataset.cardId
        if (id && !byCard.has(id)) byCard.set(id, s)
      }
      return { sig: cards.map(el => el.dataset.place).join('\n'), byPlace, byCard }
    }
    const check = () => {
      const prev = last
      const now = measure()
      last = now
      if (!prev || prev.sig === now.sig || pausedRef.current || reducedMotion()) return
      if (performance.now() - resizedAt < 500) return
      for (const el of Array.from(root.querySelectorAll<HTMLElement>('.pdc[data-place]'))) {
        const place = el.dataset.place!
        const open = openRef.current?.current
        if (open && el.dataset.cardId === open) continue
        const at = now.byPlace.get(place)!
        const stayed = prev.byPlace.get(place)
        const from = stayed ?? (el.dataset.cardId ? prev.byCard.get(el.dataset.cardId) : undefined)
        if (!from) continue
        const dx = from.x - at.x
        const dy = from.y - at.y
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue
        const flying = !stayed
        // A card mid-flight that moves again: start from where it is drawn now, not where it began.
        el.getAnimations().forEach(a => { if ((a as Animation & { id: string }).id === 'pdc-flight') a.cancel() })
        const anim = el.animate(
          [
            { transform: `translate(${dx}px, ${dy}px)${flying ? ' scale(1.04)' : ''}`, zIndex: flying ? 6 : 1 },
            { transform: 'translate(0, 0)', zIndex: flying ? 6 : 1 },
          ],
          { duration: flying ? FLY_MS : SLIDE_MS, easing: EASE },
        )
        anim.id = 'pdc-flight'
      }
    }
    last = measure()
    const mo = new MutationObserver(check)
    mo.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-place'] })
    const ro = new ResizeObserver(() => { resizedAt = performance.now(); last = measure() })
    ro.observe(root)
    return () => { mo.disconnect(); ro.disconnect() }
  }, [root])
}
